import { useState } from 'react'
import { saveBlob } from '@/api/client'
import { users as usersApi } from '@/api/endpoints'
import type { UserImportResult, UserImportRow } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { IMPORT_REASON } from '@/domain/words'
import { useSession } from '@/hooks/useSession'
import { CHIP } from '@/views/vm/chips'

// 사용자 목록 제목 줄의 계정 칩 — 체크한 사람이 아니라 플랫폼의 계정 문을 다룬다. 최고 관리자만 본다.
// `일괄 등록` 은 엑셀 한 장으로 계정을 만든다(F-77): 파일을 고르면 곧바로 줄마다 판정을 미리 보고, `등록` 이 실제로 만든다.
//   만든 계정은 승인 없이 바로 쓰이고 비밀번호는 메일 링크에서 본인이 정한다 — 그 사정은 화면이 설명하지 않는다.
// 회원가입을 받을지(F-76)는 가입 관리(`/signups`, F-121)에 있다.

type Open = 'import' | null

export function AccountChips() {
  const me = useSession()
  const [open, setOpen] = useState<Open>(null)
  if (me.role !== 'superadmin') return null
  const close = () => setOpen(null)
  return (
    <>
      <div className="flex items-center gap-1.5">
        <button type="button" className={CHIP} onClick={() => setOpen('import')}>
          일괄 등록
        </button>
      </div>
      <Dialog open={open !== null} onOpenChange={(v) => (v ? undefined : close())}>
        <DialogContent aria-describedby={undefined} className={open === 'import' ? 'sm:max-w-3xl' : undefined}>
          {open === 'import' ? <ImportForm onClose={close} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}

function ImportForm({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null)
  // 미리보기와 결과는 같은 모양이다 — `dry_run` 이 어느 쪽인지 말한다.
  const [shown, setShown] = useState<UserImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const run = async (f: File, dryRun: boolean) => {
    setBusy(true)
    setError(null)
    try {
      setShown(await usersApi.importXlsx(f, dryRun))
    } catch (err) {
      setError(err)
      if (dryRun) setShown(null)
    } finally {
      setBusy(false)
    }
  }
  const pick = (f: File | null) => {
    setFile(f)
    setShown(null)
    if (f) void run(f, true)
  }
  const template = async () => {
    setError(null)
    try {
      saveBlob(await usersApi.importTemplate(), 'users-import.xlsx')
    } catch (err) {
      setError(err)
    }
  }

  const done = shown !== null && !shown.dry_run
  const creating = shown?.rows.filter((r) => r.verdict === 'create').length ?? 0
  const skipped = (shown?.rows.length ?? 0) - creating

  return (
    <>
      <DialogHeader>
        <DialogTitle>일괄 등록</DialogTitle>
      </DialogHeader>
      {done ? null : (
        <div className="grid gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="import-file">엑셀 파일</Label>
            <button type="button" className={CHIP} onClick={() => void template()}>
              양식 내려받기
            </button>
          </div>
          <Input
            id="import-file"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={busy}
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
          />
        </div>
      )}
      {shown ? (
        <>
          <p className="text-[13px]">
            {done ? `등록됨 ${creating}명` : `등록 ${creating}명`}
            {skipped > 0 ? ` · 건너뜀 ${skipped}명` : ''}
          </p>
          {shown.rows.length > 0 ? (
            <div className="max-h-[50dvh] overflow-auto rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-12 max-sm:hidden">행</TableHead>
                    <TableHead className="w-28">이름</TableHead>
                    <TableHead className="w-28">아이디</TableHead>
                    <TableHead className="w-48 max-sm:hidden">이메일</TableHead>
                    <TableHead>결과</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.rows.map((r) => (
                    <TableRow key={r.row}>
                      <TableCell className="text-muted-foreground max-sm:hidden">{r.row}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell className="font-medium">{r.username}</TableCell>
                      <TableCell className="text-muted-foreground max-sm:hidden">{r.email}</TableCell>
                      <TableCell className="max-sm:basis-full">
                        <Verdict row={r} done={done} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </>
      ) : null}
      <Err error={error} />
      <DialogFooter>
        {done ? (
          <Button onClick={onClose}>닫기</Button>
        ) : (
          <>
            <Button variant="outline" disabled={busy} onClick={onClose}>
              취소
            </Button>
            <Button disabled={busy || !file || creating === 0} onClick={() => file && void run(file, false)}>
              {busy && shown ? '등록 중' : '등록'}
            </Button>
          </>
        )}
      </DialogFooter>
    </>
  )
}

function Verdict({ row, done }: { row: UserImportRow; done: boolean }) {
  if (row.verdict === 'skip') {
    return <span className="text-warn-ink">{row.reason ? IMPORT_REASON[row.reason] : '건너뜁니다.'}</span>
  }
  if (!done) return <span>등록</span>
  // 계정은 만들어졌지만 비밀번호 링크가 나가지 못했다 — 그 사람은 로그인 화면의 비밀번호 찾기로 다시 받는다.
  if (row.mail_sent === false) return <span className="text-warn-ink">등록됨 · 메일을 보내지 못했습니다. 비밀번호 찾기로 다시 받게 하세요.</span>
  return <span className="text-ok-ink">등록됨</span>
}

